import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import { useAnalyticsData } from './hooks/useAnalyticsData';
import AnalyticsOverview from './components/AnalyticsOverview';
import RevenueTrendChart from './components/RevenueTrendChart';
import OrderVolumeChart from './components/OrderVolumeChart';
import { ServiceTypeDistribution, OrderStatusDistribution } from './components/ServiceDistributionChart';
import TopCustomers from './components/TopCustomers';
import AvgOrderValueTrend from './components/AvgOrderValueTrend';
import DateRangeFilter from './components/DateRangeFilter';
import { computePresetRange } from './utils/presetRange';
import type { PresetRange } from './utils/presetRange';

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState<PresetRange>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const effective = useMemo(() => {
    if (preset === 'custom') return { from: dateFrom, to: dateTo };
    return computePresetRange(preset);
  }, [preset, dateFrom, dateTo]);

  const { data, loading, error, denied } = useAnalyticsData(effective.from || undefined, effective.to || undefined);

  const handlePresetChange = (p: PresetRange) => {
    setPreset(p);
    if (p !== 'custom') {
      const { from, to } = computePresetRange(p);
      setDateFrom(from);
      setDateTo(to);
    }
  };

  const handleCustomChange = (from: string, to: string) => {
    setDateFrom(from);
    setDateTo(to);
  };

  // 服务端判定无 advanced_analytics 权限：整页换成升级引导，不再请求任何数据
  if (denied) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 pb-28 md:pb-10">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">数据统计</h1>
              <p className="text-sm text-gray-400 mt-0.5">全面了解业务运营数据与趋势</p>
            </div>
          </div>
          <div className="mt-5 bg-white border border-gray-200 rounded-xl p-10 flex flex-col items-center text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-teal-50 rounded-2xl">
              <i className="ri-lock-line text-2xl text-teal-600" />
            </div>
            <p className="mt-4 text-base font-medium text-gray-800">数据统计为尊享会员专属功能</p>
            <p className="mt-1.5 text-sm text-gray-400 max-w-md">
              升级后可查看收支趋势、订单量、服务类型分布、客户排行与状态分布，
              全部指标由服务端按你的可见订单聚合，无需等待本地计算。
            </p>
            <button
              type="button"
              onClick={() => navigate('/settings')}
              className="mt-5 px-5 py-2.5 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              前往「系统设置 → 会员特权」升级
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-4 md:px-6 py-6 pb-28 md:pb-10 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">数据统计</h1>
            <p className="text-sm text-gray-400 mt-0.5">全面了解业务运营数据与趋势</p>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-time-line" />
            </div>
            数据实时更新
          </div>
        </div>

        {/* Date Range Filter */}
        <DateRangeFilter
          preset={preset}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onPresetChange={handlePresetChange}
          onCustomChange={handleCustomChange}
        />

        {error && (
          <div className="bg-rose-50 border border-rose-100 rounded-xl p-4 text-sm text-rose-600 flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-error-warning-line" /></div>
            {error}
          </div>
        )}

        {/* KPI Overview */}
        <AnalyticsOverview data={data} loading={loading} />

        {/* Revenue Trend (wide) */}
        <RevenueTrendChart data={data?.monthly ?? []} loading={loading} />

        {/* Order Volume + Service Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2">
            <OrderVolumeChart data={data?.monthly ?? []} loading={loading} />
          </div>
          <div>
            <ServiceTypeDistribution data={data?.serviceTypes ?? []} loading={loading} />
          </div>
        </div>

        {/* Avg Order Value Trend */}
        <AvgOrderValueTrend data={data?.monthly ?? []} loading={loading} />

        {/* Top Customers + Status Distribution */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <TopCustomers data={data?.customerRanks ?? []} loading={loading} />
          <OrderStatusDistribution data={data?.statusBreakdown ?? []} loading={loading} />
        </div>
      </main>
    </div>
  );
}
